import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { User as UserType } from '@supabase/supabase-js';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SignOutConfirmDialog } from "@/components/auth/SignOutConfirmDialog";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { 
  Home, 
  BookOpen, 
  LayoutDashboard, 
  DollarSign, 
  LogOut, 
  User,
  Shield,
  GraduationCap,
  FileText,
  ClipboardCheck,
  Settings
} from "lucide-react";
import { useEntitlement } from "@/hooks/useEntitlement";

interface MobileNavProps {
  user: UserType | null;
  onSignOut: () => void;
  onClose?: () => void;
}

export function MobileNav({ user, onSignOut, onClose }: MobileNavProps) {
  const { isAdmin } = useEntitlement();
  const { signOut: authSignOut } = useAuth();
  const navigate = useNavigate();
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);

  const handleNavClick = () => {
    onClose?.();
  };

  return (
    <div className="flex flex-col h-full">
      {/* User Profile Section */}
      {user && (
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-medical flex items-center justify-center">
              <User className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{user.email?.split('@')[0]}</div>
              <div className="text-sm text-muted-foreground truncate">{user.email}</div>
            </div>
          </div>
          {isAdmin && (
            <Badge variant="default" className="mt-3 w-full justify-center">
              <Shield className="h-3 w-3 mr-1" />
              Administrator
            </Badge>
          )}
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-2">
        <NavLink
          to="/"
          onClick={handleNavClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg transition-medical ${
              isActive
                ? 'bg-gradient-medical text-white'
                : 'hover:bg-muted'
            }`
          }
        >
          <Home className="h-5 w-5" />
          <span className="font-medium">Home</span>
        </NavLink>

        {user ? (
          <>
            <NavLink
              to="/study"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-medical ${
                  isActive
                    ? 'bg-gradient-medical text-white'
                    : 'hover:bg-muted'
                }`
              }
            >
              <GraduationCap className="h-5 w-5" />
              <span className="font-medium">Study Hub</span>
            </NavLink>

            <NavLink
              to="/dashboard"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-medical ${
                  isActive
                    ? 'bg-gradient-medical text-white'
                    : 'hover:bg-muted'
                }`
              }
            >
              <LayoutDashboard className="h-5 w-5" />
              <span className="font-medium">Dashboard</span>
            </NavLink>

            <NavLink
              to="/settings"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg transition-medical ${
                  isActive
                    ? 'bg-gradient-medical text-white'
                    : 'hover:bg-muted'
                }`
              }
            >
              <Settings className="h-5 w-5" />
              <span className="font-medium">Account Settings</span>
            </NavLink>

            <Separator className="my-2" />

            {/* Quick Study Actions */}
            <div className="px-2 py-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Quick Study
              </div>
              <div className="space-y-1">
                <NavLink
                  to="/practice"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted transition-medical text-sm"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>Practice MCQs</span>
                </NavLink>
                <NavLink
                  to="/notes"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted transition-medical text-sm"
                >
                  <FileText className="h-4 w-4" />
                  <span>Study Notes</span>
                </NavLink>
                <NavLink
                  to="/mock"
                  onClick={handleNavClick}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-muted transition-medical text-sm"
                >
                  <ClipboardCheck className="h-4 w-4" />
                  <span>Mock Exams</span>
                </NavLink>
              </div>
            </div>

            {isAdmin && (
              <>
                <Separator className="my-2" />
                <NavLink
                  to="/admin"
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-medical ${
                      isActive
                        ? 'bg-gradient-medical text-white'
                        : 'hover:bg-muted'
                    }`
                  }
                >
                  <Shield className="h-5 w-5" />
                  <span className="font-medium">Admin Panel</span>
                </NavLink>
              </>
            )}
          </>
        ) : null}

        <NavLink
          to="/pricing"
          onClick={handleNavClick}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-lg transition-medical ${
              isActive
                ? 'bg-gradient-medical text-white'
                : 'hover:bg-muted'
            }`
          }
        >
          <DollarSign className="h-5 w-5" />
          <span className="font-medium">Pricing</span>
        </NavLink>
      </nav>

      {/* Bottom Action */}
      {user && (
        <div className="p-4 border-t">
          <Button
            variant="destructive"
            className="w-full justify-start"
            onClick={onSignOut}
          >
            <LogOut className="h-5 w-5 mr-3" />
            Sign Out
          </Button>
        </div>
      )}

      <SignOutConfirmDialog
        open={showSignOutDialog}
        onOpenChange={setShowSignOutDialog}
        onConfirm={async () => {
          await authSignOut();
          handleNavClick();
          navigate('/');
          setShowSignOutDialog(false);
        }}
      />
    </div>
  );
}
